import { useQuery } from '@tanstack/react-query'
import { fetchStats, type LiveStats } from '../api'

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

interface Stat {
  label: string
  value: string
}

function statsFrom(data: LiveStats): Stat[] {
  return [
    { label: 'Requests (60s)', value: data.requests_60s.toLocaleString() },
    { label: 'Avg response', value: `${data.avg_response_ms.toFixed(1)} ms` },
    { label: 'Events processed (60s)', value: data.events_processed_60s.toLocaleString() },
    { label: 'Uptime', value: formatUptime(data.uptime_seconds) },
  ]
}

export function LiveMetrics() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['live-stats'],
    queryFn: fetchStats,
    refetchInterval: 10000,
  })

  // Hide the strip entirely if the stats endpoint is unreachable.
  if (isError) return null

  const stats = data ? statsFrom(data) : null

  return (
    <section aria-label="Live service metrics" className="border-y border-dash-border/70 bg-dash-surface">
      <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* "Live" is a data/status signal, so its accent stays green. */}
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#22C55E]">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22C55E] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22C55E]" />
            </span>
            Live
          </span>

          <dl className="grid grid-cols-2 gap-x-8 gap-y-4 sm:flex sm:flex-1 sm:justify-end sm:gap-x-10">
            {stats
              ? stats.map((s) => (
                  <div key={s.label} className="text-left sm:text-right">
                    <dd className="font-mono text-lg font-semibold tabular-nums text-dash-text">
                      {s.value}
                    </dd>
                    <dt className="mt-0.5 text-xs text-dash-secondary">{s.label}</dt>
                  </div>
                ))
              : isLoading &&
                ['Requests (60s)', 'Avg response', 'Events processed (60s)', 'Uptime'].map((label) => (
                    <div key={label} className="text-left sm:text-right">
                      <dd className="ml-0 h-6 w-16 animate-pulse rounded bg-dash-border sm:ml-auto" />
                      <dt className="mt-1 text-xs text-dash-secondary">{label}</dt>
                    </div>
                  ))}
          </dl>
        </div>
      </div>
    </section>
  )
}
